import { h } from 'preact';
import {
  useState,
  useEffect,
  useContext,
  useMemo
} from 'react';

import { MakeFocusableContext } from './contexts';
import { isLangInJSON } from './util';

export const useDev = (path, dev) => {
  dev = dev || { hover: {} };
  const pathString = JSON.stringify(path);
  const devActions = { ...dev.hover };

  const [devClass, setDevClass] = useState('');

  useEffect(() => {
    let newDevClass = '';

    if (dev.current === pathString || dev.highlight === pathString) {
      newDevClass += ' on-active';
    };

    setDevClass(newDevClass);
  }, [
    dev.current,
    dev.highlight,
    pathString,
  ]);
  return { devActions, devClass, pathString };
}

export const useFocusable = (...deps) => {
  const makeFocusable = useContext(MakeFocusableContext);
  useEffect(makeFocusable, deps);
}

export const useAddFocus = (containerRef, block) => {
  if (containerRef && containerRef.current && block) {
    setTimeout(() => {
      const { left: containerLeft, right: containerRight } = containerRef.current.getBoundingClientRect();
      const { right: blockRight, left: blockLeft } = block.getBoundingClientRect();

      let switchBtn;
      for (let i = 0; i < block.children.length; i++) {
        const child = block.children[i];
        if (child.getAttribute('role') === 'switch') {
          switchBtn = child;
        }
      };

      if (blockLeft < containerLeft) {
        block.classList.remove('focusable')
        if (switchBtn) {
          switchBtn.classList.remove('focusable');
        };
      } else if (blockRight > containerRight) {
        block.classList.remove('focusable');
        if (switchBtn) {
          switchBtn.classList.remove('focusable');
        };
      } else {
        block.classList.add('focusable');
        if (switchBtn) {
          switchBtn.classList.add('focusable');
        };
      };

      useContext(MakeFocusableContext)();
    }, 100);
  }
}

export const useCarousel = (containerRef, sliderRef) => {
  const [left, setLeft] = useState(0);
  const [canGoRight, setCanGoRight] = useState(false);
  const [canGoLeft, setCanGoLeft] = useState(false);

  useEffect(() => {
    if (sliderRef.current && containerRef.current) {
      //must be called inside of a timeout because animation would finish AFTER this effect is run, resulting in a stale DOM
      setTimeout(() => {
        const { left: containerLeft, right: containerRight } = containerRef.current.getBoundingClientRect();
        const blocks = sliderRef.current.children;
        for (let i = 0; i < blocks.length; i++) {
          const block = blocks[i];
          const { right: blockRight, left: blockLeft } = block.getBoundingClientRect();
          if (blockLeft < containerLeft) {
            setCanGoLeft(true)
          }

          if (blockRight > containerRight) {
            setCanGoRight(true);
            if (left >= 0) {
              //indicates we are at first carousel card, and cant go left anymore
              setCanGoLeft(false);
            };
            return
          }
        }
        setCanGoRight(false);
      }, 100)
    }
  }, [left])

  const setAdjustment = (direction) => (e) => {
    if (e.keyCode !== 13) {
      return
    }
    
    if (sliderRef.current && containerRef.current) {
      const { left: containerLeft, right: containerRight } = containerRef.current.getBoundingClientRect();

      if (direction === 'left') {
        if (left > 10) {
          return;
        };

        const blocks = sliderRef.current.children;
        for (let i = blocks.length - 1; i >= 0; i--) {
          const block = blocks[i];
          const { left: blockLeft } = block.getBoundingClientRect();
          if (blockLeft < containerLeft) {
            setLeft(left => {
              const newLeft = Math.abs(containerLeft - blockLeft) + left + (block.offsetWidth / 2);
              return newLeft > 5 ? 5 : newLeft;
            });
            return;
          }
        }
      } else if (direction === 'right') {
        const { right: sliderRight } = sliderRef.current.getBoundingClientRect()
        if ((sliderRight / containerRight) < .75) {
          return;
        }

        const blocks = sliderRef.current.children;
        for (let i = 0; i < blocks.length; i++) {
          const block = blocks[i];
          const { right: blockRight } = block.getBoundingClientRect();
          if (blockRight > containerRight) {
            setLeft(left => {
              const adjust = left - Math.abs(containerRight - blockRight) - (block.offsetWidth / 2);
              return adjust;
            });
            return;
          }
        }
      }
    }
  };

  return {
    state: [left, setAdjustment],
    canGoRight,
    canGoLeft,
    resetAdjustment: () => setLeft(5)
  };
}